import { Button, Container, Grid, Stack, Typography } from '@mui/material'
import { type ChildValidationState, type ChildShortFormLayoutState } from '../types'
import { useEffect, useState } from 'react'
import { useScrollTop } from '../../../hooks/use-scrollTop'
import { sendGet } from '../../../hooks/use-fetch'
import { type lookup } from '../../../core/types'
import { ChildShortForm } from './ChildShortForm'
import { SimpleSelectableCard } from '../../../Components/Cards/CaregiverPortal/SimpleSelectableCard'
import ListIcon from '@mui/icons-material/List'
import CheckIcon from '@mui/icons-material/Check'
import { scrollIntoView } from '../../../hooks/useScrollIntoView'

interface ChildShortFormProps {
  isdId?: string
  pageState: ChildShortFormLayoutState
  isUnborn: boolean
  isIDK: boolean
  languageList: lookup[]
  setPageState: (state: ChildShortFormLayoutState) => void
  onContinue: () => void
  onBack: () => void
}

export function ChildShortFormLayout (props: ChildShortFormProps): JSX.Element {
  useScrollTop()
  const { pageState, setPageState } = props
  const [relationshipToChildOptions, setRelationshipToChildOptions] = useState<lookup[]>([])
  const [validationState, setValidationState] = useState<ChildValidationState[]>(Array(pageState.children.length).fill({
    firstName: { isValid: true, isDirty: false },
    lastName: { isValid: true, isDirty: false },
    dateOfBirth: { isValid: true, isDirty: false },
    relationshipToChildId: { isValid: true, isDirty: false }
  }))

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const { response: relations, error } = await sendGet('/Relationship/GetLookup', {})
      if (error[0] === '') {
        setRelationshipToChildOptions(relations)
      }
    }

    if (relationshipToChildOptions.length === 0) {
      void fetchData()
    }
  }, [props.isdId])

  const handleAddChild = (): void => {
    setValidationState([...validationState, { firstName: { isValid: true, isDirty: false }, lastName: { isValid: true, isDirty: false }, dateOfBirth: { isValid: true, isDirty: false }, relationshipToChildId: { isValid: true, isDirty: false } }])
    setPageState({ ...pageState, children: [...pageState.children, { key: Date.now(), firstName: '', lastName: '', dateOfBirth: null, relationshipToChildId: null }] })
  }

  const handleUpdateState = (name: string, value: any): void => {
    const splitNameIndex = name.split('_')
    const index: number = +splitNameIndex[0] // get index of child
    const property = splitNameIndex[1] // get property to update
    const children = [...pageState.children] // Create a copy of the array
    const child = { ...children[index], [property]: value } // Update value
    children[index] = child // Update array copy
    setPageState({ ...pageState, children }) // Update the state
  }

  const handleRemoveChild = (index: number): void => {
    if (pageState.children.length > 1) {
      const children = [...pageState.children]
      const newValidationState = [...validationState]
      children.splice(index, 1)
      newValidationState.splice(index, 1)

      setPageState({ ...pageState, children })
      setValidationState(newValidationState)
    }
  }

  const handleContinue = (): void => {
    const isValid = validateForm()

    if (isValid) {
      props.onContinue()
    }
  }

  const validateFormField = (value: any, name: string): boolean => {
    if (name === 'firstName') {
      return value !== '' || props.isUnborn
    } else if (name === 'lastName') {
      return value !== ''
    } else if (name === 'dateOfBirth') {
      return value !== null || props.isUnborn
    } else if (name === 'relationshipToChildId') {
      return value !== null
    }
    return true
  }

  const validateForm = (): boolean => {
    let result = true
    let scrollTo: string | null = null
    const newValidationState: ChildValidationState[] = []
    pageState.children.forEach((child, index) => {
      const firstName = validateFormField(child.firstName, 'firstName')
      const lastName = validateFormField(child.lastName, 'lastName')
      const dateOfBirth = validateFormField(child.dateOfBirth, 'dateOfBirth')
      const relationshipToChild = validateFormField(child.relationshipToChildId, 'relationshipToChildId')
      if (firstName && lastName && dateOfBirth && relationshipToChild) {
        newValidationState.push({
          firstName: { isValid: true, isDirty: true },
          lastName: { isValid: true, isDirty: true },
          dateOfBirth: { isValid: true, isDirty: true },
          relationshipToChildId: { isValid: true, isDirty: true }
        })
      } else {
        result = false
        if (scrollTo === null) {
          scrollTo = `${index}${(!firstName ? '_firstName' : !lastName ? '_lastName' : !dateOfBirth ? '_dateOfBirth' : !relationshipToChild ? '_relationshipToChildId' : '')}`
        }
        newValidationState.push({
          firstName: { isValid: firstName, isDirty: true },
          lastName: { isValid: lastName, isDirty: true },
          dateOfBirth: { isValid: dateOfBirth, isDirty: true },
          relationshipToChildId: { isValid: relationshipToChild, isDirty: true }
        })
      }
    })

    if (pageState.continueToLongForm === undefined) {
      result = false
      scrollTo = scrollTo === null ? 'next_step' : scrollTo
    }

    if (scrollTo !== null) {
      scrollIntoView(scrollTo)
    }

    setValidationState(newValidationState)
    return result
  }

  return <Container>
    <Grid container>
      <Grid item xs={12} sx={{ my: 1 }}>
        <Typography variant='h4'>Child Information</Typography>
        <Typography variant='body1'>Please tell us a bit more about your child who needs child care.</Typography>
      </Grid>
      {pageState.children.map((child, i) => <ChildShortForm
        key={child.key}
        index={i}
        pageState={child}
        isUnborn={props.isUnborn}
        validationState={validationState[i]}
        setValidationState={(newState) => {
          const newValidationState = [...validationState]
          newValidationState[i] = newState
          setValidationState(newValidationState)
        }}
        validateFormField={validateFormField}
        relationshipToChildOptions={relationshipToChildOptions}
        languageList={props.languageList}
        isdId={props.isdId}
        onUpdate={(name, value) => { handleUpdateState(name, value) }}
        onRemove={() => { handleRemoveChild(i) }}
      />
      )}
      <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button variant='text' data-testid='add-child' onClick={handleAddChild}>+ Add Another Child</Button>
      </Grid>
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Stack
          id='next_step'
          direction='column'
          spacing={2}
          sx={{
            justifyContent: 'flex-start',
            alignItems: 'stretch'
          }}
        >
          {!props.isIDK && (
            <SimpleSelectableCard
              header='Continue to select service providers'
              description='Select this option if you want to specify the providers you would like your child to have services with.'
              imageElement={<ListIcon className='card-icon' />}
              selected={pageState.continueToLongForm ?? false}
              onClick={() => { setPageState({ ...pageState, continueToLongForm: true }) }}
            />
          )}
          <SimpleSelectableCard
            header='Submit as is'
            description='Select this option if you would like some guidance in selecting a provider.'
            imageElement={<CheckIcon className='card-icon' />}
            selected={pageState.continueToLongForm === undefined ? false : !pageState.continueToLongForm}
            onClick={() => { setPageState({ ...pageState, continueToLongForm: false }) }}
          />
        </Stack>
      </Grid>
      <Grid container item xs={12} spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={6}>
          <Button
            className='back-button'
            onClick={props.onBack}
            variant="outlined"
          >
            Back
          </Button>
        </Grid>
        <Grid item xs={6}>
          <Button
            className='footer-button'
            onClick={handleContinue}
            variant="contained"
          >
            Continue
          </Button>
        </Grid>
      </Grid>
    </Grid>
  </Container>
}
